import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "../../base/button/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../base/select/select";
/**
 * Pagination controls for DataTable component.
 * Provides page navigation and page size selection.
 *
 * @example
 * ```tsx
 * <DataTablePagination table={table} pageSizeOptions={[10, 20, 50]} />
 * ```
 */ export function DataTablePagination({ table, pageSizeOptions = [
    10,
    20,
    30,
    40,
    50
] }) {
    const { t } = useTranslation();
    const currentPage = table.state.pagination.pageIndex + 1;
    const totalPages = table.getPageCount();
    const pageSize = table.state.pagination.pageSize;
    // Get both filtered and total counts
    const filteredCount = table.getFilteredRowModel().rows.length;
    const totalCount = table.getCoreRowModel().rows.length;
    const isFiltered = filteredCount < totalCount;
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex items-center justify-between px-2",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "flex-1 text-sm text-muted-foreground",
                children: table.getFilteredSelectedRowModel().rows.length > 0 ? /*#__PURE__*/ _jsx("span", {
                    children: t("dataTable.rowsSelected", {
                        count: table.getFilteredSelectedRowModel().rows.length,
                        total: filteredCount
                    })
                }) : /*#__PURE__*/ _jsx("span", {
                    children: isFiltered ? t("dataTable.filteredResults", {
                        count: filteredCount,
                        filtered: filteredCount,
                        total: totalCount
                    }) : t("dataTable.totalResults", {
                        count: filteredCount
                    })
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center space-x-2",
                children: [
                    /*#__PURE__*/ _jsxs(Button, {
                        variant: "outline",
                        className: "hidden h-8 w-8 p-0 lg:flex",
                        onClick: ()=>table.setPageIndex(0),
                        disabled: !table.getCanPreviousPage(),
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "sr-only",
                                children: t("dataTable.pagination.goToFirstPage")
                            }),
                            /*#__PURE__*/ _jsx(ChevronsLeft, {
                                className: "h-4 w-4"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Button, {
                        variant: "outline",
                        className: "h-8 w-8 p-0",
                        onClick: ()=>table.previousPage(),
                        disabled: !table.getCanPreviousPage(),
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "sr-only",
                                children: t("dataTable.pagination.goToPreviousPage")
                            }),
                            /*#__PURE__*/ _jsx(ChevronLeft, {
                                className: "h-4 w-4"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex w-[100px] items-center justify-center text-sm font-medium",
                        children: t("dataTable.page", {
                            current: currentPage,
                            total: totalPages
                        })
                    }),
                    /*#__PURE__*/ _jsxs(Button, {
                        variant: "outline",
                        className: "h-8 w-8 p-0",
                        onClick: ()=>table.nextPage(),
                        disabled: !table.getCanNextPage(),
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "sr-only",
                                children: t("dataTable.pagination.goToNextPage")
                            }),
                            /*#__PURE__*/ _jsx(ChevronRight, {
                                className: "h-4 w-4"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Button, {
                        variant: "outline",
                        className: "hidden h-8 w-8 p-0 lg:flex",
                        onClick: ()=>table.setPageIndex(table.getPageCount() - 1),
                        disabled: !table.getCanNextPage(),
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "sr-only",
                                children: t("dataTable.pagination.goToLastPage")
                            }),
                            /*#__PURE__*/ _jsx(ChevronsRight, {
                                className: "h-4 w-4"
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-1 items-center justify-end space-x-2",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm font-medium",
                        children: t("dataTable.rowsPerPage")
                    }),
                    /*#__PURE__*/ _jsxs(Select, {
                        value: `${pageSize}`,
                        onValueChange: (value)=>{
                            table.setPageSize(Number(value));
                        },
                        children: [
                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                className: "h-8 w-[70px]",
                                children: /*#__PURE__*/ _jsx(SelectValue, {
                                    placeholder: pageSize
                                })
                            }),
                            /*#__PURE__*/ _jsx(SelectContent, {
                                side: "top",
                                children: pageSizeOptions.map((size)=>/*#__PURE__*/ _jsx(SelectItem, {
                                        value: `${size}`,
                                        children: size
                                    }, size))
                            })
                        ]
                    })
                ]
            })
        ]
    });
}

//# sourceMappingURL=DataTablePagination.js.map